import { useFrame } from "@react-three/fiber";
import { type ReactNode, useRef } from "react";
import { Box3, type Object3D } from "three";
import { useDragStore } from "../stores/dragStore";
import { computeProductBoundingBox } from "./productViewerCameraFit";

interface ProductFloorAlignerProps {
  children: ReactNode;
  floorY?: number;
  scale?: number;
}

const THRESHOLD = 0.0001;

/**
 * Continuously monitors the bounding box of product geometry (excluding
 * measurement overlays and drag helpers) and adjusts the group's Y position
 * so the product bottom sits at the specified floor level.
 *
 * Uses localMin (= worldBottom - position.y) which is invariant to our own
 * position changes, so this cannot cause an infinite loop.
 */
export const ProductFloorAligner = ({
  children,
  floorY = 0,
  scale = 1 / 1000,
}: ProductFloorAlignerProps) => {
  const groupRef = useRef<Object3D>(null);
  const lastLocalMinRef = useRef<number | null>(null);
  const boxRef = useRef(new Box3());

  useFrame(() => {
    if (!groupRef.current) return;

    // Skip the full-scene bounding box traversal while a drag is active: it
    // runs every frame and competes with the drag rebuild. Read the store
    // imperatively to avoid re-rendering this component (and its whole product
    // subtree). Idle frames resume immediately after the drag ends, which
    // re-aligns the floor automatically.
    if (useDragStore.getState().isDragActive) return;

    // Force-update all descendant world matrices so newly mounted
    // meshes have correct transforms before bounding box computation
    groupRef.current.updateMatrixWorld(true);

    computeProductBoundingBox(groupRef.current, boxRef.current);
    if (boxRef.current.isEmpty()) return;

    const worldBottom = boxRef.current.min.y;
    const currentY = groupRef.current.position.y;
    const localMin = worldBottom - currentY;

    if (
      lastLocalMinRef.current === null ||
      Math.abs(localMin - lastLocalMinRef.current) > THRESHOLD
    ) {
      lastLocalMinRef.current = localMin;
      groupRef.current.position.y = floorY - localMin;
    }
  });

  return (
    <group ref={groupRef} scale={scale}>
      {children}
    </group>
  );
};
